import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Layout } from "../../components/layout";
import { Select } from "../../components/Select";
import { TextInput } from "../../components/TextInput";
import { PlusIcon } from "../../components/Icons";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
  AppointmentUpdateData,
  StepUpdateData,
} from "../../services/treatmentPlanService";
import { cdtCodesService, CDTCode } from "../../services/cdtCodesService";
import {
  useModal,
  ReApproveConfirmationModalContent,
  UpdateStatusConfirmationModalContent,
} from "../../components/Modal";
import {
  EditableAppointment,
  EditableAppointmentData,
  EditableProcedureData,
} from "./components";
import { SelectOption } from "../../components/Select/Select";
import {
  PageMeta,
  FormSection,
  FormRow,
  SectionTitle,
  StepsContainer,
  FooterContainer,
  DraggableAppointmentWrapper,
} from "./EditTreatmentPlanPage.styled";
import { colors } from "../../theme/colors";
import { useNavigationBlocker } from "../../hooks";
import { useNavigationBlockerContext } from "../../contexts";
import { FormErrorContainer } from "../../components/FormErrorContainer";
import { RichTextEditor } from "../../components/RichTextEditor";

const REAPPROVE_MODAL_ID = "reapprove-confirmation-modal";
const UPDATE_STATUS_MODAL_ID = "update-status-confirmation-modal";

const EditTreatmentPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openModal, closeModal, updateModal } = useModal();

  // All status labels for display
  const ALL_STATUS_LABELS: Record<number, string> = useMemo(
    () => ({
      0: t("treatmentPlan.status.generating"),
      1: t("treatmentPlan.status.aiDrafted"),
      2: t("treatmentPlan.status.awaitingProviderReview"),
      3: t("treatmentPlan.status.awaitingPatientConfirmation"),
      4: t("treatmentPlan.status.declinedByPatient"),
      5: t("treatmentPlan.status.approvedByPatient"),
      6: t("treatmentPlan.status.inProgress"),
      7: t("treatmentPlan.status.completed"),
    }),
    [t],
  );

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const fetchInitiatedRef = useRef(false);
  const [draggingAppointmentIndex, setDraggingAppointmentIndex] = useState<
    number | null
  >(null);
  const [dragOverAppointmentIndex, setDragOverAppointmentIndex] = useState<
    number | null
  >(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Original data for comparison
  const [originalPlan, setOriginalPlan] =
    useState<TreatmentPlanResponse | null>(null);

  // Form state
  const [planName, setPlanName] = useState("");
  const [planStatus, setPlanStatus] = useState<number>(1);
  const [treatmentSummary, setTreatmentSummary] = useState("");
  const [providerNote, setProviderNote] = useState("");
  const [appointments, setAppointments] = useState<EditableAppointmentData[]>(
    [],
  );

  // Check if status editing is allowed (only for Approved by Patient (5) and In Progress (6))
  const canEditStatus = useMemo(() => {
    if (!originalPlan) return false;
    return originalPlan.status === 5 || originalPlan.status === 6;
  }, [originalPlan]);

  // Status options based on current original status
  // Approved by Patient (5) -> can only change to In Progress (6)
  // In Progress (6) -> can only change to Completed (7)
  const STATUS_OPTIONS: SelectOption[] = useMemo(() => {
    if (!originalPlan) return [];
    if (originalPlan.status === 5) {
      return [{ value: 6, label: t("treatmentPlan.status.inProgress") }];
    }
    if (originalPlan.status === 6) {
      return [{ value: 7, label: t("treatmentPlan.status.completed") }];
    }
    return [];
  }, [originalPlan, t]);

  // Current status label for placeholder (shown when current status is not in dropdown options)
  const currentStatusLabel = useMemo(() => {
    return ALL_STATUS_LABELS[planStatus] || "";
  }, [ALL_STATUS_LABELS, planStatus]);

  // CDT codes (used for ID lookup when saving)
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);

  // Validation errors
  interface ValidationErrors {
    planName?: string;
    appointments?: {
      [appointmentIndex: number]: {
        title?: string;
        description?: string;
        timeframeValue?: string;
        procedures?: {
          [procedureIndex: number]: {
            text?: string;
            description?: string;
            fee?: string;
            durationMinutes?: string;
          };
        };
      };
    };
  }
  const [errors, setErrors] = useState<ValidationErrors>({});
  // Track if user has attempted to save (to enable real-time validation)
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Track which appointments should be expanded due to errors
  const [expandedAppointments, setExpandedAppointments] = useState<Set<number>>(
    new Set(),
  );
  // Counter to force re-expansion on each validation attempt
  const [validationAttempt, setValidationAttempt] = useState(0);

  // Convert API appointment to editable appointment
  const convertToEditableAppointment = useCallback(
    (
      apiAppointment: TreatmentPlanResponse["appointments"][0],
    ): EditableAppointmentData => {
      return {
        id: apiAppointment.id,
        title: apiAppointment.title,
        timeframeValue: apiAppointment.timeframeLabel || "",
        timeframeUnit: "", // Deprecated, kept for interface compatibility
        conditionalPath: apiAppointment.conditionalPath || "",
        description: apiAppointment.description || "",
        sortOrder: apiAppointment.sortOrder,
        procedures: apiAppointment.steps.map((step) => {
          // Explanation comes from CDT code descriptions
          const explanation = step.codeSuggestions
            .map((cs) => cs.description)
            .filter(Boolean)
            .join("\n");
          const defaultFee = step.codeSuggestions.reduce(
            (sum, cs) => sum + (cs.defaultFee || 0),
            0,
          );

          const codeSuggestionCodes = step.codeSuggestions.map((cs) => cs.code);
          return {
            id: step.id,
            text: step.name, // Step name
            description: step.description || "", // Step description
            sortOrder: step.sortOrder,
            cdtCodes: codeSuggestionCodes,
            customProcedureId: undefined,
            resolvedCdtCodes: codeSuggestionCodes,
            explanation, // Read-only, from CDT codes
            durationMinutes: step.durationMinutes ?? 20,
            fee: String(step.fee ?? defaultFee),
            isFeeEdited: false,
          };
        }),
      };
    },
    [],
  );

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      if (fetchInitiatedRef.current) return;

      fetchInitiatedRef.current = true;

      try {
        setLoading(true);
        setError(null);
        // Fetch treatment plan and CDT codes in parallel
        const [planData, codesData] = await Promise.all([
          treatmentPlanService.getById(id),
          cdtCodesService.getCDTCodes({ activeOnly: true }),
        ]);

        // Redirect to view page if treatment plan is Completed (status 7)
        if (planData.status === 7) {
          navigate(`/treatment-plan/${id}`, { replace: true });
          return;
        }

        setOriginalPlan(planData);
        setPlanName(planData.name);
        setPlanStatus(planData.status);
        setTreatmentSummary(planData.treatmentSummary);
        setProviderNote(planData.providerNote || "");
        setAppointments(
          planData.appointments.map(convertToEditableAppointment),
        );
        setCdtCodes(codesData);
      } catch (err) {
        setError(t("editTreatmentPlan.failedToLoad"));
        console.error("Error fetching treatment plan:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, convertToEditableAppointment]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!originalPlan) return false;

    if (planName !== originalPlan.name) return true;
    if (planStatus !== originalPlan.status) return true;
    if (treatmentSummary !== originalPlan.treatmentSummary) return true;
    if (providerNote !== (originalPlan.providerNote || "")) return true;

    // Check appointments changes
    if (appointments.length !== originalPlan.appointments.length) return true;

    for (let i = 0; i < appointments.length; i++) {
      const originalAppointment = originalPlan.appointments[i];
      const currentAppointment = appointments[i];

      if (!originalAppointment) return true;

      // Check timeframe changes
      if (
        currentAppointment.timeframeValue !==
        (originalAppointment.timeframeLabel || "")
      )
        return true;

      if (
        currentAppointment.conditionalPath !==
        (originalAppointment.conditionalPath || "")
      )
        return true;
      if (
        currentAppointment.description !==
        (originalAppointment.description || "")
      )
        return true;

      // Check procedures changes
      if (
        currentAppointment.procedures.length !==
        originalAppointment.steps.length
      )
        return true;

      for (let j = 0; j < currentAppointment.procedures.length; j++) {
        const originalStep = originalAppointment.steps[j];
        const currentProcedure = currentAppointment.procedures[j];

        if (!originalStep) return true;
        // Check step name
        if (currentProcedure.text !== originalStep.name) return true;
        // Check step description
        if (
          (currentProcedure.description || "") !==
          (originalStep.description || "")
        )
          return true;

        // Check CDT codes
        const originalCdtCodes = originalStep.codeSuggestions.map(
          (cs) => cs.code,
        );
        if (
          currentProcedure.resolvedCdtCodes.length !== originalCdtCodes.length
        )
          return true;
        for (let k = 0; k < currentProcedure.resolvedCdtCodes.length; k++) {
          if (currentProcedure.resolvedCdtCodes[k] !== originalCdtCodes[k])
            return true;
        }

        // Check durationMinutes - use step.durationMinutes from API if available, otherwise default 20
        const originalDuration = originalStep.durationMinutes ?? 20;
        if (Number(currentProcedure.durationMinutes) !== originalDuration)
          return true;

        // Check fee - use step.fee from API if available, otherwise calculate from CDT codes
        const originalFee =
          originalStep.fee ??
          originalStep.codeSuggestions.reduce(
            (sum, cs) => sum + (cs.defaultFee || 0),
            0,
          );
        if (parseFloat(currentProcedure.fee) !== originalFee) return true;
      }
    }

    return false;
  }, [
    originalPlan,
    planName,
    planStatus,
    treatmentSummary,
    providerNote,
    appointments,
  ]);

  const hasAppointmentUpdates = useMemo(() => {
    if (!originalPlan) return false;
    if (appointments.length !== originalPlan.appointments.length) return true;

    for (let i = 0; i < appointments.length; i++) {
      const orig = originalPlan.appointments[i];
      const curr = appointments[i];
      if (!orig) return true;

      if (curr.timeframeValue !== (orig.timeframeLabel || "")) return true;
      if (curr.conditionalPath !== (orig.conditionalPath || "")) return true;
      if (curr.description !== (orig.description || "")) return true;
      if (curr.procedures.length !== orig.steps.length) return true;

      for (let j = 0; j < curr.procedures.length; j++) {
        const origStep = orig.steps[j];
        const currProc = curr.procedures[j];
        if (!origStep) return true;
        if (currProc.text !== origStep.name) return true;
        if ((currProc.description || "") !== (origStep.description || ""))
          return true;

        const origCdtCodes = origStep.codeSuggestions.map((cs) => cs.code);
        if (currProc.resolvedCdtCodes.length !== origCdtCodes.length)
          return true;
        for (let k = 0; k < currProc.resolvedCdtCodes.length; k++) {
          if (currProc.resolvedCdtCodes[k] !== origCdtCodes[k]) return true;
        }

        const origDuration = origStep.durationMinutes ?? 20;
        if (Number(currProc.durationMinutes) !== origDuration) return true;

        const origFee =
          origStep.fee ??
          origStep.codeSuggestions.reduce(
            (sum, cs) => sum + (cs.defaultFee || 0),
            0,
          );
        if (parseFloat(currProc.fee) !== origFee) return true;
      }
    }
    return false;
  }, [originalPlan, appointments]);

  // Use navigation blocker to handle unsaved changes
  // This handles browser back/forward buttons, programmatic navigation, and browser refresh/close
  const { confirmNavigation } = useNavigationBlocker({
    shouldBlock: hasChanges,
  });

  const { setConfirmNavigation } = useNavigationBlockerContext();

  useEffect(() => {
    setConfirmNavigation(confirmNavigation);
    return () => {
      setConfirmNavigation(null);
    };
  }, [confirmNavigation, setConfirmNavigation]);

  // Format date
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })}   ${date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  // Handle appointment change
  const handleAppointmentChange = useCallback(
    (
      appointmentIndex: number,
      field: keyof EditableAppointmentData,
      value: unknown,
    ) => {
      setAppointments((prev) =>
        prev.map((appointment, index) => {
          if (index === appointmentIndex) {
            return { ...appointment, [field]: value };
          }
          return appointment;
        }),
      );
    },
    [],
  );

  // Handle procedure change within an appointment
  const handleProcedureChange = useCallback(
    (
      appointmentIndex: number,
      procedureIndex: number,
      field: keyof EditableProcedureData,
      value: unknown,
    ) => {
      setAppointments((prev) =>
        prev.map((appointment, aIndex) => {
          if (aIndex === appointmentIndex) {
            const updatedProcedures = appointment.procedures.map(
              (procedure, pIndex) => {
                if (pIndex === procedureIndex) {
                  const updated = { ...procedure, [field]: value };
                  if (field === "fee") updated.isFeeEdited = true;
                  return updated;
                }
                return procedure;
              },
            );
            return { ...appointment, procedures: updatedProcedures };
          }
          return appointment;
        }),
      );
    },
    [],
  );

  // Handle add procedure to appointment
  const handleAddProcedure = useCallback((appointmentIndex: number) => {
    setAppointments((prev) =>
      prev.map((appointment, index) => {
        if (index === appointmentIndex) {
          const newProcedure: EditableProcedureData = {
            text: "",
            description: "",
            sortOrder: appointment.procedures.length + 1,
            cdtCodes: [],
            customProcedureId: undefined,
            resolvedCdtCodes: [],
            explanation: "",
            durationMinutes: 20,
            fee: "0",
            isFeeEdited: false,
          };
          return {
            ...appointment,
            procedures: [...appointment.procedures, newProcedure],
          };
        }
        return appointment;
      }),
    );
  }, []);

  // Handle delete procedure from appointment
  const handleDeleteProcedure = useCallback(
    (appointmentIndex: number, procedureIndex: number) => {
      setAppointments((prev) =>
        prev.map((appointment, aIndex) => {
          if (aIndex === appointmentIndex) {
            const filteredProcedures = appointment.procedures.filter(
              (_, pIndex) => pIndex !== procedureIndex,
            );
            // Renumber procedures
            const renumberedProcedures = filteredProcedures.map(
              (procedure, pIndex) => ({
                ...procedure,
                sortOrder: pIndex + 1,
              }),
            );
            return { ...appointment, procedures: renumberedProcedures };
          }
          return appointment;
        }),
      );
    },
    [],
  );

  // Handle appointment delete
  const handleAppointmentDelete = useCallback((appointmentIndex: number) => {
    setAppointments((prev) => {
      const filtered = prev.filter((_, index) => index !== appointmentIndex);
      // Renumber appointments
      return filtered.map((appointment, index) => ({
        ...appointment,
        sortOrder: index + 1,
      }));
    });
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragItem.current = index;
    setDraggingAppointmentIndex(index);

    // Set the entire appointment wrapper as the drag image
    const dragHandle = e.currentTarget as HTMLElement;
    const appointmentWrapper = dragHandle.closest(
      "[data-appointment-wrapper]",
    ) as HTMLElement;
    if (appointmentWrapper) {
      const rect = appointmentWrapper.getBoundingClientRect();
      e.dataTransfer.setDragImage(appointmentWrapper, rect.width / 2, 20);
    }
  }, []);

  // Handle drag over - use this for visual feedback
  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (
        draggingAppointmentIndex !== null &&
        draggingAppointmentIndex !== index
      ) {
        dragOverItem.current = index;
        setDragOverAppointmentIndex(index);
      }
    },
    [draggingAppointmentIndex],
  );

  // Handle drag leave
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if leaving to outside, not to child elements
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverAppointmentIndex(null);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (dragItem.current !== null && dragItem.current !== index) {
      setAppointments((prev) => {
        const newAppointments = [...prev];
        const draggedItem = newAppointments[dragItem.current!];
        newAppointments.splice(dragItem.current!, 1);
        newAppointments.splice(index, 0, draggedItem);

        // Renumber appointments
        return newAppointments.map((appointment, idx) => ({
          ...appointment,
          sortOrder: idx + 1,
        }));
      });
    }

    // Reset all drag states
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingAppointmentIndex(null);
    setDragOverAppointmentIndex(null);
  }, []);

  // Handle drag end (cleanup)
  const handleDragEnd = useCallback(() => {
    // Reset all drag states
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingAppointmentIndex(null);
    setDragOverAppointmentIndex(null);
  }, []);

  // Handle add appointment
  const handleAddAppointment = useCallback(() => {
    const newAppointment: EditableAppointmentData = {
      title: "",
      timeframeValue: "",
      timeframeUnit: "", // Deprecated
      conditionalPath: "",
      description: "",
      sortOrder: appointments.length + 1,
      procedures: [],
    };
    setAppointments((prev) => [...prev, newAppointment]);
  }, [appointments.length]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: ValidationErrors = {};
    const appointmentsToExpand = new Set<number>();
    let hasErrors = false;

    // Validate plan name
    if (!planName.trim()) {
      newErrors.planName = t("editTreatmentPlan.validation.nameRequired");
      hasErrors = true;
    } else if (planName.length > 50) {
      newErrors.planName = t("editTreatmentPlan.validation.nameTooLong");
      hasErrors = true;
    }

    // Validate appointments and procedures
    appointments.forEach((appointment, appointmentIndex) => {
      const appointmentErrors: ValidationErrors["appointments"] = {};
      let hasAppointmentErrors = false;

      // Validate appointment description (always required)
      if (!appointment.description?.trim()) {
        if (!appointmentErrors[appointmentIndex]) {
          appointmentErrors[appointmentIndex] = {};
        }
        appointmentErrors[appointmentIndex].description = t(
          "editTreatmentPlan.validation.appointmentDescriptionRequired",
        );
        hasAppointmentErrors = true;
      }

      // Validate timeframeValue (required for all appointments except the last one)
      const isLastAppointment = appointmentIndex === appointments.length - 1;
      if (!isLastAppointment && !appointment.timeframeValue?.trim()) {
        if (!appointmentErrors[appointmentIndex]) {
          appointmentErrors[appointmentIndex] = {};
        }
        appointmentErrors[appointmentIndex].timeframeValue = t(
          "editTreatmentPlan.validation.timeframeRequired",
        );
        hasAppointmentErrors = true;
      }

      // Validate procedures
      appointment.procedures.forEach((procedure, procedureIndex) => {
        const procedureErrors: {
          text?: string;
          description?: string;
          fee?: string;
          durationMinutes?: string;
        } = {};
        let hasProcedureErrors = false;

        // Validate step name
        if (!procedure.text?.trim()) {
          procedureErrors.text = t(
            "editTreatmentPlan.validation.stepNameRequired",
          );
          hasProcedureErrors = true;
        }

        // Validate step description
        if (!procedure.description?.trim()) {
          procedureErrors.description = t(
            "editTreatmentPlan.validation.stepDescriptionRequired",
          );
          hasProcedureErrors = true;
        }

        // Validate fee
        if (
          !procedure.fee ||
          procedure.fee.trim() === "" ||
          parseFloat(procedure.fee) < 0
        ) {
          procedureErrors.fee = t("editTreatmentPlan.validation.feeRequired");
          hasProcedureErrors = true;
        }

        // Validate duration
        if (!procedure.durationMinutes || procedure.durationMinutes <= 0) {
          procedureErrors.durationMinutes = t(
            "editTreatmentPlan.validation.durationRequired",
          );
          hasProcedureErrors = true;
        }

        if (hasProcedureErrors) {
          if (!appointmentErrors[appointmentIndex]) {
            appointmentErrors[appointmentIndex] = {};
          }
          if (!appointmentErrors[appointmentIndex].procedures) {
            appointmentErrors[appointmentIndex].procedures = {};
          }
          appointmentErrors[appointmentIndex].procedures![procedureIndex] =
            procedureErrors;
          hasAppointmentErrors = true;
        }
      });

      if (hasAppointmentErrors) {
        newErrors.appointments = {
          ...newErrors.appointments,
          ...appointmentErrors,
        };
        appointmentsToExpand.add(appointmentIndex);
        hasErrors = true;
      }
    });

    setErrors(newErrors);

    // Expand appointments that have errors (always update to trigger re-expansion)
    setExpandedAppointments(appointmentsToExpand);
    if (appointmentsToExpand.size > 0) {
      // Increment counter to force re-expansion even if same appointments have errors
      setValidationAttempt((prev) => prev + 1);
    }

    return !hasErrors;
  }, [planName, appointments, t]);

  // Re-run validation when form data changes (only after first save attempt)
  useEffect(() => {
    if (hasAttemptedSave) {
      validateForm();
    }
  }, [hasAttemptedSave, validateForm]);

  // Handle save
  const handleSave = useCallback(async () => {
    setHasAttemptedSave(true);
    if (!validateForm() || !id || !originalPlan) return;

    setSaving(true);
    try {

      // Convert appointments to API format
      const apiAppointments: AppointmentUpdateData[] = appointments.map(
        (appointment, index) => {
          // Build timeframeLabel from value only
          const timeframeLabel =
            index < appointments.length - 1 ? appointment.timeframeValue : "";

          const appointmentData: AppointmentUpdateData = {
            title:
              appointment.title ||
              t("treatmentPlan.appointment", { number: index + 1 }),
            timeframeLabel,
            conditionalPath: appointment.conditionalPath || undefined,
            description: appointment.description || undefined,
            sortOrder: appointment.sortOrder,
            steps: appointment.procedures.map((procedure) => {
              const stepData: StepUpdateData = {
                name: procedure.text,
                description: procedure.description || undefined,
                sortOrder: procedure.sortOrder,
                durationMinutes: procedure.durationMinutes || undefined,
                fee:
                  procedure.fee !== "" ? parseFloat(procedure.fee) : undefined,
                isFeeEdited: procedure.isFeeEdited,
                codeSuggestions: procedure.resolvedCdtCodes
                  .map((code) => {
                    const found = cdtCodes.find((c) => c.code === code);
                    return found ? { cdtCodeId: found.id } : null;
                  })
                  .filter((s): s is { cdtCodeId: number } => s !== null),
              };

              // Only include id for existing steps (id must be >= 1)
              if (procedure.id && procedure.id >= 1) {
                stepData.id = procedure.id;
              }

              return stepData;
            }),
          };

          // Only include id for existing appointments (id must be >= 1)
          if (appointment.id && appointment.id >= 1) {
            appointmentData.id = appointment.id;
          }

          return appointmentData;
        },
      );

      // If status is AI Drafted (1), change to Awaiting Provider Review (2) on save
      const statusToSave = originalPlan.status === 1 ? 2 : planStatus;

      await treatmentPlanService.update(id, {
        id: originalPlan.id,
        name: planName,
        treatmentSummary,
        providerNote: providerNote || undefined,
        status: statusToSave,
        appointments: apiAppointments,
        hasAppointmentUpdates,
      });

      navigate(`/treatment-plan/${id}`, { replace: true });
    } catch (err) {
      console.error("Error saving treatment plan:", err);
      setError(t("editTreatmentPlan.failedToSave"));
    } finally {
      setSaving(false);
    }
  }, [
    id,
    originalPlan,
    planName,
    planStatus,
    treatmentSummary,
    providerNote,
    appointments,
    cdtCodes,
    validateForm,
    navigate,
    hasAppointmentUpdates,
    t,
  ]);

  // Check if status allows re-approval (3, 4, 5, 6 = Awaiting Patient Confirmation, Declined by Patient, Approved by Patient, In Progress)
  const isReApprovalStatus = useMemo(() => {
    if (!originalPlan) return false;
    return [3, 4, 5, 6].includes(originalPlan.status);
  }, [originalPlan]);

  // Handle re-approve (save with status change to 3 - Awaiting Patient Confirmation)
  const handleReApprove = useCallback(async () => {
    setHasAttemptedSave(true);
    if (!validateForm() || !id || !originalPlan) return;

    setSaving(true);
    try {
      // Convert appointments to API format
      const apiAppointments: AppointmentUpdateData[] = appointments.map(
        (appointment, index) => {
          // Build timeframeLabel from value only
          const timeframeLabel =
            index < appointments.length - 1 ? appointment.timeframeValue : "";

          const appointmentData: AppointmentUpdateData = {
            title:
              appointment.title ||
              t("treatmentPlan.appointment", { number: index + 1 }),
            timeframeLabel,
            conditionalPath: appointment.conditionalPath || undefined,
            description: appointment.description || undefined,
            sortOrder: appointment.sortOrder,
            steps: appointment.procedures.map((procedure) => {
              const stepData: StepUpdateData = {
                name: procedure.text,
                description: procedure.description || undefined,
                sortOrder: procedure.sortOrder,
                durationMinutes: procedure.durationMinutes || undefined,
                fee:
                  procedure.fee !== "" ? parseFloat(procedure.fee) : undefined,
                isFeeEdited: procedure.isFeeEdited,
                codeSuggestions: procedure.resolvedCdtCodes
                  .map((code) => {
                    const found = cdtCodes.find((c) => c.code === code);
                    return found ? { cdtCodeId: found.id } : null;
                  })
                  .filter((s): s is { cdtCodeId: number } => s !== null),
              };

              // Only include id for existing steps (id must be >= 1)
              if (procedure.id && procedure.id >= 1) {
                stepData.id = procedure.id;
              }

              return stepData;
            }),
          };

          // Only include id for existing appointments (id must be >= 1)
          if (appointment.id && appointment.id >= 1) {
            appointmentData.id = appointment.id;
          }

          return appointmentData;
        },
      );

      await treatmentPlanService.update(id, {
        id: originalPlan.id,
        name: planName,
        treatmentSummary,
        providerNote: providerNote || undefined,
        status: 3,
        appointments: apiAppointments,
        hasAppointmentUpdates,
      });

      closeModal(REAPPROVE_MODAL_ID);
      navigate(`/treatment-plan/${id}`, { replace: true });
    } catch (err) {
      console.error("Error re-approving treatment plan:", err);
      setError(t("editTreatmentPlan.failedToSave"));
    } finally {
      setSaving(false);
    }
  }, [
    id,
    originalPlan,
    planName,
    treatmentSummary,
    providerNote,
    appointments,
    cdtCodes,
    validateForm,
    navigate,
    closeModal,
    hasAppointmentUpdates,
    t,
  ]);

  // Open re-approve confirmation modal
  const handleReApproveClick = useCallback(() => {
    setHasAttemptedSave(true);
    if (!validateForm()) return;

    openModal({
      id: REAPPROVE_MODAL_ID,
      title: "",
      content: (
        <ReApproveConfirmationModalContent
          onClose={() => closeModal(REAPPROVE_MODAL_ID)}
          onReApprove={handleReApprove}
          onLoadingChange={(loading) =>
            updateModal(REAPPROVE_MODAL_ID, { closeOnBackdropClick: !loading })
          }
        />
      ),
      showCloseButton: false,
      closeOnBackdropClick: true,
      maxWidth: "600px",
      containerPadding: "60px 90px",
      contentPadding: "0",
    });
  }, [
    openModal,
    closeModal,
    updateModal,
    handleReApprove,
    setHasAttemptedSave,
    validateForm,
  ]);

  // Check if this is a status change scenario (5→6 or 6→7)
  const isStatusChangeScenario = useMemo(() => {
    if (!originalPlan) return false;
    // Approved by Patient (5) -> In Progress (6)
    // In Progress (6) -> Completed (7)
    return (
      (originalPlan.status === 5 && planStatus === 6) ||
      (originalPlan.status === 6 && planStatus === 7)
    );
  }, [originalPlan, planStatus]);

  // Generate list of validation error messages for display
  const validationErrorMessages = useMemo(() => {
    const messages: string[] = [];

    if (errors.planName) {
      messages.push(errors.planName);
    }

    if (errors.appointments) {
      Object.entries(errors.appointments).forEach(
        ([appointmentIndex, appointmentErrors]) => {
          const appointmentNum = parseInt(appointmentIndex) + 1;

          if (appointmentErrors.description) {
            messages.push(
              `${t("treatmentPlan.appointment", { number: appointmentNum })}: ${appointmentErrors.description}`,
            );
          }

          if (appointmentErrors.timeframeValue) {
            messages.push(
              `${t("treatmentPlan.appointment", { number: appointmentNum })}: ${appointmentErrors.timeframeValue}`,
            );
          }

          if (appointmentErrors.procedures) {
            Object.entries(appointmentErrors.procedures).forEach(
              ([procedureIndex, procedureErrors]) => {
                const stepNum = parseInt(procedureIndex) + 1;
                const prefix = `${t("treatmentPlan.appointment", { number: appointmentNum })} → ${t("editTreatmentPlan.step", { number: stepNum })}`;

                if (procedureErrors.text) {
                  messages.push(`${prefix}: ${procedureErrors.text}`);
                }
                if (procedureErrors.description) {
                  messages.push(`${prefix}: ${procedureErrors.description}`);
                }
                if (procedureErrors.fee) {
                  messages.push(`${prefix}: ${procedureErrors.fee}`);
                }
                if (procedureErrors.durationMinutes) {
                  messages.push(
                    `${prefix}: ${procedureErrors.durationMinutes}`,
                  );
                }
              },
            );
          }
        },
      );
    }

    return messages;
  }, [errors, t]);

  // Handle status change (save with updated status)
  const handleStatusChange = useCallback(async () => {
    setHasAttemptedSave(true);
    if (!validateForm() || !id || !originalPlan) return;

    setSaving(true);
    try {

      // Convert appointments to API format
      const apiAppointments: AppointmentUpdateData[] = appointments.map(
        (appointment, index) => {
          // Build timeframeLabel from value only
          const timeframeLabel =
            index < appointments.length - 1 ? appointment.timeframeValue : "";

          const appointmentData: AppointmentUpdateData = {
            title:
              appointment.title ||
              t("treatmentPlan.appointment", { number: index + 1 }),
            timeframeLabel,
            conditionalPath: appointment.conditionalPath || undefined,
            description: appointment.description || undefined,
            sortOrder: appointment.sortOrder,
            steps: appointment.procedures.map((procedure) => {
              const stepData: StepUpdateData = {
                name: procedure.text,
                description: procedure.description || undefined,
                sortOrder: procedure.sortOrder,
                durationMinutes: procedure.durationMinutes || undefined,
                fee:
                  procedure.fee !== "" ? parseFloat(procedure.fee) : undefined,
                isFeeEdited: procedure.isFeeEdited,
                codeSuggestions: procedure.resolvedCdtCodes
                  .map((code) => {
                    const found = cdtCodes.find((c) => c.code === code);
                    return found ? { cdtCodeId: found.id } : null;
                  })
                  .filter((s): s is { cdtCodeId: number } => s !== null),
              };

              // Only include id for existing steps (id must be >= 1)
              if (procedure.id && procedure.id >= 1) {
                stepData.id = procedure.id;
              }

              return stepData;
            }),
          };

          // Only include id for existing appointments (id must be >= 1)
          if (appointment.id && appointment.id >= 1) {
            appointmentData.id = appointment.id;
          }

          return appointmentData;
        },
      );

      await treatmentPlanService.update(id, {
        id: originalPlan.id,
        name: planName,
        treatmentSummary,
        providerNote: providerNote || undefined,
        status: planStatus,
        appointments: apiAppointments,
        hasAppointmentUpdates,
      });

      closeModal(UPDATE_STATUS_MODAL_ID);
      navigate(`/treatment-plan/${id}`, { replace: true });
    } catch (err) {
      console.error("Error updating treatment plan status:", err);
      setError(t("editTreatmentPlan.failedToSave"));
    } finally {
      setSaving(false);
    }
  }, [
    id,
    originalPlan,
    planName,
    planStatus,
    treatmentSummary,
    providerNote,
    appointments,
    cdtCodes,
    validateForm,
    navigate,
    closeModal,
    hasAppointmentUpdates,
    t,
  ]);

  // Open status change confirmation modal
  const handleStatusChangeClick = useCallback(() => {
    openModal({
      id: UPDATE_STATUS_MODAL_ID,
      title: "",
      content: (
        <UpdateStatusConfirmationModalContent
          onCancel={() => closeModal(UPDATE_STATUS_MODAL_ID)}
          onConfirm={handleStatusChange}
          isLoading={saving}
        />
      ),
      showCloseButton: true,
      maxWidth: "600px",
      containerPadding: "60px 90px",
      contentPadding: "0",
    });
  }, [openModal, closeModal, handleStatusChange, saving]);

  // Handle go back / cancel with unsaved changes check
  const handleCancel = useCallback(() => {
    confirmNavigation(() => {
      navigate(`/treatment-plan/${id}`, { replace: true });
    });
  }, [confirmNavigation, navigate, id]);

  // Get patient name from treatment plan response
  const getPatientName = () => {
    if (originalPlan?.patientFirstName || originalPlan?.patientLastName) {
      return `${originalPlan.patientFirstName || ""} ${originalPlan.patientLastName || ""}`.trim();
    }
    return `Patient #${originalPlan?.patientUid || ""}`;
  };

  const patientName = getPatientName();

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Layout>
    );
  }

  if (error || !originalPlan) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <div>{error || t("editTreatmentPlan.notFound")}</div>
          <Button onClick={() => navigate(-1)}>
            {t("editTreatmentPlan.goBack")}
          </Button>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h1" sx={{ marginBottom: "8px" }}>
        {t("editTreatmentPlan.pageTitle", { patientName })}
      </Typography>
      <PageMeta>
        {formatDateTime(originalPlan.dateGenerated)}
        {originalPlan.approvedByPatientAt &&
          ` | ${t("editTreatmentPlan.approvedByPatient", {
            date: new Date(originalPlan.approvedByPatientAt).toLocaleDateString(
              "en-US",
              {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              },
            ),
          })}`}
      </PageMeta>

      <FormSection>
        <FormRow>
          <Box sx={{ flex: 1 }}>
            <TextInput
              name="treatment-plan-name"
              label={t("editTreatmentPlan.treatmentPlanName")}
              placeholder={t("editTreatmentPlan.treatmentPlanNamePlaceholder")}
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              required
              error={!!errors.planName}
              helperText={errors.planName}
              maxLength={50}
            />
          </Box>
          {canEditStatus && (
            <Box sx={{ width: "200px" }}>
              <Select
                name="treatment-plan-status"
                label={t("editTreatmentPlan.treatmentPlanStatus")}
                value={planStatus}
                onChange={(value) => setPlanStatus(value as number)}
                options={STATUS_OPTIONS}
                placeholder={currentStatusLabel}
              />
            </Box>
          )}
        </FormRow>
      </FormSection>

      <FormSection>
        <SectionTitle>{t("editTreatmentPlan.treatmentSummary")}</SectionTitle>
        <RichTextEditor
          value={treatmentSummary}
          onChange={setTreatmentSummary}
          placeholder={t("editTreatmentPlan.treatmentSummaryPlaceholder")}
          minHeight={100}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>{t("editTreatmentPlan.noteFromProvider")}</SectionTitle>
        <TextInput
          name="provider-note"
          placeholder={t("editTreatmentPlan.noteFromProviderPlaceholder")}
          value={providerNote}
          onChange={(e) => setProviderNote(e.target.value)}
          multiline
          rows={2}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>
          {t("editTreatmentPlan.appointments")}
          <Button
            size="small"
            variant="contained"
            onClick={handleAddAppointment}
            sx={{ ml: 2 }}
          >
            <PlusIcon width="20px" height="20px" fill="#FFFFFF" />
            {t("editTreatmentPlan.addAppointment")}
          </Button>
        </SectionTitle>

        <StepsContainer>
          {appointments
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((appointment, index) => (
              <DraggableAppointmentWrapper
                key={appointment.id || `new-${index}`}
                data-appointment-wrapper
                isDragging={draggingAppointmentIndex === index}
                isDragOver={dragOverAppointmentIndex === index}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <EditableAppointment
                  appointment={appointment}
                  appointmentNumber={index + 1}
                  isLastAppointment={index === appointments.length - 1}
                  defaultExpanded={index === 0}
                  forceExpanded={expandedAppointments.has(index)}
                  forceExpandTrigger={validationAttempt}
                  validationErrors={errors.appointments?.[index]}
                  onAppointmentDragStart={(e) => handleDragStart(e, index)}
                  onChange={(field, value) =>
                    handleAppointmentChange(index, field, value)
                  }
                  onProcedureChange={(procedureIndex, field, value) =>
                    handleProcedureChange(index, procedureIndex, field, value)
                  }
                  onAddProcedure={() => handleAddProcedure(index)}
                  onDeleteProcedure={(procedureIndex) =>
                    handleDeleteProcedure(index, procedureIndex)
                  }
                  onDelete={() => handleAppointmentDelete(index)}
                />
              </DraggableAppointmentWrapper>
            ))}
        </StepsContainer>
      </FormSection>

      <FormErrorContainer errors={validationErrorMessages} />

      <FooterContainer>
        <Button variant="outlined" onClick={handleCancel}>
          {t("editTreatmentPlan.cancel")}
        </Button>
        {isStatusChangeScenario ? (
          // Scenario 3: Status change (5→6 or 6→7) - yellow Save Changes button
          <Button
            variant="contained"
            onClick={handleStatusChangeClick}
            disabled={
              saving || !hasChanges || validationErrorMessages.length > 0
            }
            sx={{
              backgroundColor: colors.amber.main,
              "&:hover": {
                backgroundColor: colors.amber.dark,
              },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("editTreatmentPlan.saveChanges")
            )}
          </Button>
        ) : isReApprovalStatus ? (
          // Scenario 2: Re-approval (status 3, 4, 5, 6) - green Save & Re-Approve button
          <Button
            variant="contained"
            onClick={handleReApproveClick}
            disabled={
              saving || !hasChanges || validationErrorMessages.length > 0
            }
            sx={{
              backgroundColor: colors.status.success,
              "&:hover": {
                backgroundColor: "#388E3C",
              },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              t("editTreatmentPlan.saveAndReApprove")
            )}
          </Button>
        ) : (
          // Default: regular Save Changes button
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving || !hasChanges || validationErrorMessages.length > 0
            }
          >
            {saving ? (
              <CircularProgress size={20} />
            ) : (
              t("editTreatmentPlan.saveChanges")
            )}
          </Button>
        )}
      </FooterContainer>
    </Layout>
  );
};

export default EditTreatmentPlanPage;
